import { Box, Button, CircularProgress, Dialog, FormControl, FormControlLabel, IconButton, Paper, Radio, RadioGroup, TextField, Typography, useTheme } from '@mui/material'
import React, { useEffect, useState, useCallback } from 'react'

import noTutorialsImg from '../../assets/norecording.svg'
import api from '../../service/api'
import { getFileSizeFromBytes, Icons } from '../../utils/utilities'
import { ErrorMessage, Field, Form, Formik } from 'formik'
import * as Yup from 'yup'
import Progress from '../../components/common/Progress'
import materialUploadImg from '../../assets/upload-assignment.svg'
import { useRef } from 'react'
import { toast } from 'react-toastify'
import VideoPlayer from '../../components/common/VideoPlayer'
import ConfirmDialog from '../../components/ConfirmDialog'

const initialValues = {
    title: "",
    description: "",
    file: null,
    module: "",
    tutorial_for: "FACULTY"
}

const validationSchema = Yup.object().shape( {
    title: Yup.string().required( "Title is required." ),
    module: Yup.string().required( "Module is required." ),
    file: Yup.mixed().required( "File is required." ),
} )

const TutorialBox = ( { tutorial, setSelectedTutorial, getTutorials } ) => {

    const [deleting, setDeleting] = useState( false )
    const [previewPlaying, setPreviewPlaying] = useState( false )
    const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState( false )

    const { palette, border } = useTheme()

    const previewVideoRef = useRef()


    const handleOnMouseLeavePreviewVideo = async () => {
        try {
            if ( previewVideoRef.current ) {
                previewVideoRef.current.muted = true
                if ( !previewVideoRef.current.paused )
                    await previewVideoRef.current?.pause()
            }
        } catch ( err ) {
            console.log( "Failed to pause preview video" )
        }
    }

    const handleOnMouseOverPreviewVideo = async () => {
        try {
            if ( previewVideoRef.current ) {
                previewVideoRef.current.muted = true
                if ( previewVideoRef.current.paused )
                    await previewVideoRef.current?.play()
            }
        } catch ( err ) {
            console.log( "Failed to pause preview video" )
        }
    }

    const deleteTutorial = async ( e ) => {
        try {
            e.stopPropagation()
            setDeleting( true )
            await api.tutorials.deleteTutorial( tutorial.id )
            getTutorials()
            setShowDeleteConfirmDialog( false )
        } catch ( err ) {
            toast( err?.response?.data?.message || "Failed to delete tutorial!" )
        } finally {
            setDeleting( false )
        }
    }



    return (
        <Box onClick={() => setSelectedTutorial( tutorial )} overflow="clip" borderRadius="10px" border={border[1]} key={tutorial.id}>

            {showDeleteConfirmDialog && <ConfirmDialog waiting={deleting} status={showDeleteConfirmDialog} cancelAction={() => setShowDeleteConfirmDialog( false )} message={`Confirm you want to delete ${tutorial.title}`} confirmAction={deleteTutorial} actionName="Yes, delete it" />}

            <Box sx={{ cursor: "pointer", width: "100%", aspectRatio: "16 / 9" }} onMouseOver={handleOnMouseOverPreviewVideo} onMouseEnter={handleOnMouseOverPreviewVideo} onMouseLeave={handleOnMouseLeavePreviewVideo} position="relative" width="100%"  >
                <Box onPlay={() => setPreviewPlaying( true )} onPause={() => setPreviewPlaying( false )} ref={previewVideoRef} component="video" src={tutorial.signedUrl} width="100%" sx={{ objectFit: "fit", width: "100%", aspectRatio: "16 / 9" }} />
                {!previewPlaying && <Box fontSize="35px" color="white" position="absolute" sx={{ inset: "0" }} bgcolor="rgba(0,0,0,0.6)" display="flex" flexDirection="column" height="100%" alignItems="center" justifyContent="center" component="span">
                    <Box component="span" className='remove-on-hover' >{Icons.default.PlayCircleFilledWhiteIcon}</Box>
                    <Typography variant='subtitle2' component="span" fontSize="10px" textAlign="center" >
                        {previewPlaying ? "Playing..." : "Bring mouse over to preview"}
                    </Typography>
                </Box>}
            </Box>
            <Box padding="10px">
                <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                        fontSize: '18px',
                        lineHeight: '1em',

                        /* two-line clamp */
                        display: '-webkit-box',
                        WebkitLineClamp: 2,         // <- number of lines
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        // optional: preserve word-break behavior
                        whiteSpace: 'normal',
                    }}
                >
                    {tutorial.title}
                </Typography>
                <Box display="flex" gap="10px" alignItems="center">
                    <Typography borderRadius="10px" width="fit-content" variant='subtitle2' fontSize="12px" bgcolor={palette.contentBg} border={border[1]} padding="2px 5px" >{tutorial.module}</Typography>
                    <Typography borderRadius="10px" width="fit-content" variant='subtitle2' fontSize="12px" bgcolor={palette.contentBg} border={border[1]} padding="2px 5px" >{tutorial.tutorial_for}</Typography>
                </Box>
                <Box marginTop="20px" display="flex" gap="10px" alignItems="center">
                    <Button sx={{ textTransform: "capitalize" }} variant='contained' startIcon={Icons.default.EditIcon} color='secondary' disableElevation size="small" >Edit</Button>
                    <Button onClick={( e ) => {
                        e.stopPropagation()
                        setShowDeleteConfirmDialog( true )
                    }} sx={{ textTransform: "capitalize" }} startIcon={Icons.default.DeleteIcon} color='error' disableElevation size="small" >Delete</Button>
                </Box>
            </Box>
        </Box>
    )
}

const Support = () => {

    const [error, setError] = useState( null )
    const [loading, setLoading] = useState( true )
    const [progress, setProgress] = useState( 0 )
    const [addMode, setAddMode] = useState( false )
    const [tutorials, setTutorials] = useState( [] )
    const [uploading, setUploading] = useState( false )
    const [previewPlaying, setPreviewPlaying] = useState( false )
    const [dragOverStatus, setDragOverStatus] = useState( false )
    const [selectedTutorial, setSelectedTutorial] = useState( null )

    const { border, palette, shadows } = useTheme()

    const fileInputRef = useRef()
    const previewVideoRef = useRef()

    const handleOnMouseLeavePreviewVideo = async () => {
        try {
            if ( previewVideoRef.current ) {
                previewVideoRef.current.muted = true
                if ( !previewVideoRef.current.paused )
                    await previewVideoRef.current?.pause()
            }
        } catch ( err ) {
            console.log( "Failed to pause preview video" )
        }
    }

    const handleOnMouseOverPreviewVideo = async () => {
        try {
            if ( previewVideoRef.current ) {
                previewVideoRef.current.muted = true
                if ( previewVideoRef.current.paused )
                    await previewVideoRef.current?.play()
            }
        } catch ( err ) {
            console.log( "Failed to pause preview video" )
        }
    }

    const getTutorials = useCallback( async () => {
        try {
            setLoading( true )
            const { data: { data: tutorials } } = await api.tutorials.fetchTutorials()
            setTutorials( tutorials )
            setError( null )
        } catch ( err ) {
            console.log( err )
            setError( { code: err?.response.status || 500, message: err?.response?.data?.message || "SOmething went wrong while fetching tutorials." } )
        } finally {
            setLoading( false )
        }
    }, [] )

    const addTutorial = async ( values, { resetForm } ) => {
        const formData = new FormData()
        formData.append( 'module', values.module )
        for ( const key in values ) {
            if ( !Object.hasOwn( values, key ) || key === "module" ) continue
            const value = values[key]
            formData.append( key, value )
        }
        try {
            setUploading( true )
            await api.tutorials.uploadTutorials( formData )
            getTutorials()
            setAddMode( false )
        } catch ( err ) {
            console.log( err )
            toast( err?.response?.data?.message || "Something went wrong. Failed to upload tutorial!" )
        } finally {
            setUploading( false )
        }

    }

    const handleFilesSelect = ( event, setFieldValue ) => {
        const file = event.target.files[0]
        console.log( file )
        const blobUrl = URL.createObjectURL( file )
        file.blobUrl = blobUrl
        setFieldValue( 'file', file )
    }

    useEffect( () => {
        getTutorials()
    }, [getTutorials] )

    return (
        <Box overflow="auto" padding={{ xs: "10px", md: "20px" }}>
            <Paper sx={{ flexGrow: 1 }}>
                {selectedTutorial && <Dialog open={selectedTutorial} slotProps={{ paper: { sx: { width: "95%", maxWidth: "1024px" } } }} >
                    <Box borderBottom={border[1]} padding="10px 20px" display="flex" justifyContent="space-between" gap="20px" alignItems="center" >
                        <Typography variant='h6'>{selectedTutorial.title}</Typography>
                        <IconButton size='small' onClick={() => setSelectedTutorial( null )} >
                            {Icons.default.CloseIcon}
                        </IconButton>
                    </Box>
                    <Box display="flex" justifyContent="center" alignItems="center">
                        <VideoPlayer defaults={{ volumn: 1, start: 10 }} src={selectedTutorial.signedUrl} />
                    </Box>
                    <Box padding="10px 20px">
                        <Typography>{selectedTutorial.description || "No description!"}</Typography>
                    </Box>
                </Dialog>}
                <Dialog slotProps={{ paper: { sx: { width: "95%", maxWidth: "1024px" } } }} open={addMode} onClose={() => setAddMode( false )} >
                    <Box borderBottom={border[1]} padding="10px 20px" display="flex" justifyContent="space-between" gap="20px" alignItems="center" >
                        <Typography variant='h6'>Add New Tutorial</Typography>
                        <IconButton size='small' onClick={() => setAddMode( false )} >
                            {Icons.default.CloseIcon}
                        </IconButton>
                    </Box>
                    <Box padding="10px 20px" >
                        <Formik validationSchema={validationSchema} initialValues={initialValues} onSubmit={addTutorial} >
                            {( { setFieldValue, values } ) => (
                                <Form>
                                    <FormControl fullWidth>
                                        <Typography gutterBottom variant='subtitle2' color={palette.labelColor}>Title for the tutorial *</Typography>
                                        <Field size="small" as={TextField} placeholder="Eg: How to create a new Exam" type="text" name="title" />
                                        <Typography fontSize="12px" color='errorMessage.main'><ErrorMessage name='title' /></Typography>
                                    </FormControl>
                                    <FormControl margin='normal' fullWidth>
                                        <Typography gutterBottom variant='subtitle2' color={palette.labelColor}>Description for the tutorial (optional)</Typography>
                                        <Field size="small" as={TextField} multiline rows={4} placeholder="Eg: This tutorial describes how to create a new Exam from Alive platform." type="text" name="description" />
                                        <Typography fontSize="12px" color='errorMessage.main'><ErrorMessage name='description' /></Typography>
                                    </FormControl>
                                    <FormControl margin='normal' fullWidth>
                                        <Typography gutterBottom variant='subtitle2' color={palette.labelColor}>To which module does this tutorial corresponds to? *</Typography>
                                        <Field size="small" as={TextField} placeholder="Eg: Exam" type="text" name="module" />
                                        <Typography fontSize="12px" color='errorMessage.main'><ErrorMessage name='module' /></Typography>
                                    </FormControl>
                                    <FormControl margin='normal' fullWidth>
                                        <Typography gutterBottom variant='subtitle2' color={palette.labelColor}>To which user role does this tutorial help? *</Typography>
                                        <Box display="flex" gap="10px">
                                            <RadioGroup dir='row' sx={{
                                                margin: 0,
                                                marginLeft: "10px",
                                                display: "grid",
                                                gap: "10px",
                                                gridTemplateColumns: { md: "auto auto", xs: "1fr" }
                                            }} value={values.tutorial_for} onChange={event => setFieldValue( 'tutorial_for', event.target.value )} >
                                                <FormControlLabel control={<Radio size='small' />} value="FACULTY" sx={{ boxShadow: values.tutorial_for === "FACULTY" && shadows[1], border: border[1], borderRadius: "10px", padding: "5px 10px" }} label="Faculty" />
                                                <FormControlLabel control={<Radio size='small' />} value="STUDENT" sx={{ boxShadow: values.tutorial_for === "STUDENT" && shadows[1], border: border[1], borderRadius: "10px", padding: "5px 10px" }} label="Student" />
                                            </RadioGroup>
                                        </Box>
                                    </FormControl>
                                    {!values.file && <FormControl margin='normal' fullWidth>
                                        <Typography gutterBottom variant='subtitle2' color={palette.labelColor}>Select the tutorial file *</Typography>
                                        <Box
                                            onDragLeave={() => setDragOverStatus( false )}
                                            onDrop={() => setDragOverStatus( false )}
                                            onDragEnter={() => setDragOverStatus( true )}
                                            onDragOver={() => setDragOverStatus( true )}
                                            position="relative"
                                            borderRadius="5px"
                                            gap="10px"
                                            display="flex"
                                            padding="20px"
                                            flexDirection="column"
                                            alignItems="center"
                                            justifyContent="center"
                                            minHeight="150px"
                                            border={`${dragOverStatus ? '3px' : '3px'} dashed ${dragOverStatus ? palette.primary.dark : palette.primary.main}`}
                                            bgcolor={`${palette.primary.light}11`}
                                        >
                                            <Box component="input" ref={fileInputRef} accept=".mp4,.webp" disabled={uploading} style={{ position: "absolute", cursor: uploading ? "not-allowed" : "pointer", opacity: 0, top: 0, left: 0, width: "100%", height: "100%" }} title="" type="file" name="file" id="file" onInput={( e ) => { handleFilesSelect( e, setFieldValue ) }} />
                                            {<Box display="flex" flexDirection="column" gap="10px" alignItems="center">
                                                <Box component="img" src={materialUploadImg} width="180px" alt="Upload tutorial file" />
                                                {!dragOverStatus && !uploading && <Typography color="textSecondary" fontSize="14px">Drag and drop to upload file </Typography>}
                                                {!dragOverStatus && !uploading && <Button onClick={() => fileInputRef.current?.click()} variant='contained' disableElevation color="customThemeColor"  >
                                                    Browse Files
                                                </Button>}
                                                {!dragOverStatus && !uploading && <Typography variant="body2" color="textSecondary" textAlign="center">Accepted formats: {["mp4", "webm"].join( ", " )} </Typography>}
                                                {dragOverStatus && <Typography sx={{ pointerEvents: "none" }} className='animation-shake' fontWeight="600" variant='body1'>Drop your materials here!</Typography>}
                                                {dragOverStatus && <Typography variant="subtitle2" color="textSecondary" fontSize="12px">Supported formats: {["mp4", "webm"].join( ", " )}</Typography>}
                                            </Box>}
                                        </Box>
                                        <Typography fontSize="12px" color='errorMessage.main'><ErrorMessage name='file' /></Typography>
                                    </FormControl>}
                                    {values.file && <Box marginTop="10px" border={border[1]} bgcolor={palette.contentBg} display="flex" alignItems="flex-start" gap="10px" borderRadius="10px" overflow="clip">
                                        <Box sx={{ cursor: "pointer", ":hover .remove-on-hover": { display: "none" } }} onMouseOver={handleOnMouseOverPreviewVideo} onMouseEnter={handleOnMouseOverPreviewVideo} onMouseLeave={handleOnMouseLeavePreviewVideo} height="90px" position="relative">
                                            <Box onPlay={() => setPreviewPlaying( true )} onPause={() => setPreviewPlaying( false )} ref={previewVideoRef} component="video" sx={{ objectFit: "fit" }} height="100%" src={values.file.blobUrl} />
                                            <Box fontSize="35px" color="white" position="absolute" sx={{ inset: "0" }} bgcolor="rgba(0,0,0,0.6)" display="flex" flexDirection="column" height="100%" alignItems="center" justifyContent="center" component="span">
                                                <Box component="span" className='remove-on-hover' >{Icons.default.PlayCircleFilledWhiteIcon}</Box>
                                                <Typography variant='subtitle2' component="span" fontSize="10px" textAlign="center" >
                                                    {previewPlaying ? "Playing..." : "Bring mouse over to preview"}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box>
                                            <Typography variant='subtitle2' >{values.file.name}</Typography>
                                            <Typography variant='subtitle2' fontSize="12px" color="textSecondary" >{getFileSizeFromBytes( values.file.size )}</Typography>
                                            <Button onClick={() => setFieldValue( 'file', null )} sx={{ textTransform: "capitalize", marginTop: "10px" }} variant='outlined' color="secondary" size='small' startIcon={Icons.default.Replay} >Replace file</Button>
                                        </Box>
                                    </Box>}
                                    <Box display="flex" marginTop="20px" gap="10px">
                                        <Button disabled={uploading} type='submit' variant='contained' disableElevation color="secondary" startIcon={uploading ? <CircularProgress size={20} /> : Icons.default.AddIcon} sx={{ width: "max-content", textTransform: "capitalize", textTransform: "capitalize" }} >Add Tutorial</Button>
                                        <Button disabled={uploading} variant='contained' onClick={() => setAddMode( false )} disableElevation color="errorMessage" startIcon={Icons.default.CloseIcon} sx={{ width: "max-content", textTransform: "capitalize", textTransform: "capitalize" }} >Cancel</Button>
                                    </Box>
                                </Form>
                            )}
                        </Formik>
                    </Box>
                </Dialog>


                <Box padding="20px" borderBottom={border[1]} display="flex" alignItems="flex-end" justifyContent="space-between">
                    <Box>
                        <Typography variant='h5'>Tutorials</Typography>
                        <Typography variant='subtitle2' color="textSecondary">Manage tutorial resources.</Typography>
                    </Box>
                    <Button startIcon={Icons.default.AddIcon} onClick={() => setAddMode( true )} sx={{ textTransform: "capitalize" }} variant='contained' disableElevation >
                        Add new Tutorial
                    </Button>
                </Box>
                <Box padding="20px">
                    {tutorials.length === 0 && <Box padding="20px" borderRadius="10px" display="flex" alignItems="center" flexDirection="column" gap="10px" bgcolor={palette.contentBg}>
                        <Box component="img" src={noTutorialsImg} alt='No tutorials uploaded' width="150px" />
                        <Typography variant='subtitle2' textAlign="center" >Tutorials are yet to be uploaded.</Typography>
                    </Box>}
                    {tutorials.length > 0 && <Box borderRadius="10px" display="grid" alignItems="flex-start" flexDirection="column" gap="10px" gridTemplateColumns={{ md: "repeat(4,1fr)", xs: "repeat(1,1fr)", sm: "repeat(2,1fr)" }} >
                        {tutorials.map( tutorial => (
                            <TutorialBox getTutorials={getTutorials} setSelectedTutorial={setSelectedTutorial} tutorial={tutorial} />
                        ) )}
                    </Box>}
                </Box>
            </Paper>
        </Box>
    )
}

export default Support
